import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BillingContext = createContext(null);

export function BillingProvider({ children }) {
  // P0 fix: state hydration on app startup.
  //
  // Before this fix, BillingProvider fired `/billing/me` on mount IN PARALLEL
  // with AuthProvider's bootstrapping `/auth/me`. If the auth refresh-token
  // flow hadn't completed yet, the billing call would 401 silently and
  // `billing` stayed `null` forever — components reading `effective_plan ||
  // "free"` (TrialBlock, MapTab, PlanLock) treated paid users as Free and
  // blurred their premium features until the user manually visited /app/plans
  // (which re-fetched billing). See handoff summary for full bug context.
  //
  // The fix has three parts:
  //   1. `loading` starts at TRUE (we genuinely DON'T know the plan yet —
  //      consumers must wait, not fall back to Free).
  //   2. Refresh is gated on auth state: only fire when `user` is truthy
  //      AND not in the bootstrapping `null` state. When the user logs out
  //      (`user === false`), we clear billing immediately to prevent stale
  //      plan leakage across sessions.
  //   3. `initialized` flag tells consumers "first fetch has resolved at
  //      least once" — distinguishes "we're still booting" from "we tried
  //      and the API failed".
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [upgradeContext, setUpgradeContext] = useState(null); // { reason, message }

  // Resilient refresh: when `/billing/me` fails we used to swallow the
  // error completely → `billing` stayed null → `has()` returned false →
  // paid users saw the fake "Modulo riservato" upgrade screen until they
  // manually navigated to /app/plans (which re-fetched billing).
  //
  // Two changes here:
  //   1. Retry with backoff (250ms → 800ms → 2s). Most failures are
  //      transient (cold-start race with the auth refresh cycle, brief
  //      network hiccup, sleeping CF worker). Three tries clears the
  //      vast majority of false-negatives.
  //   2. Surface the last error via `billingError` so consumers can
  //      tell "we tried and failed" apart from "user is genuinely free".
  //      Currently used by debug logging; could feed a retry banner.
  const refresh = useCallback(async () => {
    setLoading(true);
    setBillingError(null);
    const delays = [0, 250, 800, 2000]; // 4 attempts total
    let lastErr = null;
    for (let attempt = 0; attempt < delays.length; attempt += 1) {
      if (delays[attempt] > 0) {
        await new Promise((r) => setTimeout(r, delays[attempt]));
      }
      try {
        const { data } = await api.get(`/billing/me?_=${Date.now()}`);
        setBilling(data);
        setLoading(false);
        setInitialized(true);
        setBillingError(null);
        return data;
      } catch (err) {
        lastErr = err;
        // 401/403 mean "not authenticated" — there's no point in retrying,
        // bail out fast and let the auth flow take over.
        const status = err?.response?.status;
        if (status === 401 || status === 403) break;
      }
    }
    // All attempts exhausted (or auth error). Keep last good `billing`
    // if we had one — partial degradation is better than collapsing to
    // "free" mid-session.
    if (lastErr) {
      // eslint-disable-next-line no-console
      console.warn("[billing] /billing/me failed after retries:", lastErr?.message || lastErr);
      setBillingError(lastErr);
    }
    setLoading(false);
    setInitialized(true);
  }, []);

  // Initial load — gated on auth ready (user resolved to truthy).
  // When the user logs out, drop the billing snapshot immediately so a
  // subsequent login on a different account never reuses the previous
  // plan's gates.
  useEffect(() => {
    if (user === null) {
      // Auth still bootstrapping — keep `loading=true`, do nothing yet.
      return;
    }
    if (user === false) {
      // Anonymous / logged out → clear cached billing.
      setBilling(null);
      setLoading(false);
      setInitialized(true);
      return;
    }
    // Authenticated user → fetch billing.
    refresh();
  }, [user, refresh]);

  // Auto-refresh when the user comes back to the tab (admin may have changed
  // their plan in the meantime). Skip when logged out to avoid noisy 401s.
  useEffect(() => {
    if (!user || user === false) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    const onFocus = () => refresh();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh, user]);

  // Listen for 402 plan_limit events broadcast by axios interceptor
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      const reason =
        detail.limit_kind === "clients" ? "limit_clients" :
        detail.limit_kind === "contracts" ? "limit_contracts" :
        detail.limit_kind === "trial_send" ? "trial_send" :
        detail.limit_kind === "trial_share" ? "trial_share" :
        "generic";
      setUpgradeContext({ reason, message: detail.message });
    };
    window.addEventListener("plan-limit-hit", handler);
    return () => window.removeEventListener("plan-limit-hit", handler);
  }, []);

  const openUpgrade = useCallback((ctx) => setUpgradeContext(ctx || { reason: "generic" }), []);
  const closeUpgrade = useCallback(() => setUpgradeContext(null), []);

  const isPlan = (...plans) => billing && plans.includes(billing.effective_plan);
  const has = (moduleName) => {
    if (!billing) return false;
    return (billing.limits?.modules || []).includes(moduleName);
  };

  // True until the first billing fetch resolves (success OR failure) AFTER
  // auth has settled. Consumers should wait on this before gating premium
  // features — otherwise paid users see the "free" fallback on cold load.
  const isHydrating = user === null || (!initialized && loading);

  const value = useMemo(() => ({
    billing,
    loading,
    initialized,
    isHydrating,
    billingError,
    refresh,
    openUpgrade,
    closeUpgrade,
    upgradeContext,
    isPlan,
    has,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [billing, loading, initialized, isHydrating, billingError, upgradeContext, refresh, openUpgrade, closeUpgrade]);

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling outside BillingProvider");
  return ctx;
}
